import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { navigate } from 'libs/router';

import CampaignBriefForm from '../campaign-brief-form/CampaignBriefForm';

import { resetCampaignBrief, saveCampaignBrief } from 'modules/campaigns/reducers/campaignBrief';
import { resetAssets, saveAssets } from 'modules/campaigns/reducers/campaignAssets';
import { resetSubcampaigns } from 'modules/campaigns/reducers/campaignSubcampaigns';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { isAdmin as isAdminAccount } from 'utils/account';

import type { CampaignAsset, CampaignForm } from 'types';

applyTranslations(require('./campaign-create.i18n.json'));

interface ConfirmationModalParams {
  title: string,
  message: string,
  onConfirm: () => void,
  onCancel: () => void
}

type OwnProps = {
  profileId: number
};

type StateProps = {
  campaignAssets: {
    assets: Array<CampaignAsset>,
    mainImage: CampaignAsset | undefined | null,
    isUpdated: boolean
  },
  currentOrganizationId: number,
  isAdmin: boolean
};

type DispatchProps = {
  dispatchResetCampaignBrief: () => Promise<any>,
  dispatchResetAssets: () => Promise<any>,
  dispatchResetSubcampaigns: () => Promise<any>,
  dispatchSaveCampaignBrief: (b: number, a: CampaignForm) => Promise<any>,
  dispatchSaveAssets: (a: number) => Promise<any>,
  dispatchShowConfirmationModal: (
    params: ConfirmationModalParams
  ) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

class CampaignCreate extends React.Component<Props> {
  componentDidMount() {
    const { dispatchResetCampaignBrief, dispatchResetAssets, dispatchResetSubcampaigns } = this.props;

    dispatchResetCampaignBrief();
    dispatchResetAssets();
    dispatchResetSubcampaigns();
  }

  handleSaveCampaignBrief = (
    briefDetails: CampaignForm
  ): void => {
    const { profileId, dispatchSaveCampaignBrief, dispatchSaveAssets } = this.props;

    dispatchSaveCampaignBrief(profileId, briefDetails)
      .then((campaign) => { // eslint-disable-line arrow-body-style
        return dispatchSaveAssets(campaign.id)
          .catch((error) => { logError(error); }) // no images is okayish
          .then(() => navigate.to(`/campaigns/${campaign.id}/participants/shortlisted`));
      })
      .catch(() => this.scrollToErrorMessage());
  };

  handleSubmit = (briefDetails: CampaignForm): void => {
    const {
      isAdmin,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;
    const zalandoMetaBrands = briefDetails.zalandoMeta?.brands;

    if (isAdmin && isEmpty(zalandoMetaBrands)) {
      dispatchShowConfirmationModal({
        title: __t('campaign_create.modals.brands_on_zalando_fashion_store.title'),
        message: __t('campaign_create.modals.brands_on_zalando_fashion_store.message'),
        onConfirm: () => {
          this.handleSaveCampaignBrief(briefDetails);
          dispatchHideConfirmationModal();
        },
        onCancel: () => dispatchHideConfirmationModal()
      });
    } else {
      this.handleSaveCampaignBrief(briefDetails);
    }
  };

  handleCancel = (): void => {
    const { currentOrganizationId } = this.props;

    navigate.to(`/organizations/${currentOrganizationId}/campaigns`);
  };

  scrollToErrorMessage() {
    window.scrollTo(0, 0);
  }

  render() {
    const { campaignAssets } = this.props;

    return (
      <CampaignBriefForm
        title={__t('campaign_create.title_create')}
        campaignAssets={campaignAssets}
        campaign={null}
        onSubmit={this.handleSubmit}
        onCancel={this.handleCancel}
      />
    );
  }
}

const mapStateToProps = (state): StateProps => ({
  campaignAssets: state.campaignAssets,
  currentOrganizationId: state.organizations.currentOrganizationId,
  isAdmin: isAdminAccount(state.account.currentAccount, state.account.roles)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetCampaignBrief: () => dispatch(resetCampaignBrief()),
  dispatchResetAssets: () => dispatch(resetAssets()),
  dispatchResetSubcampaigns: () => dispatch(resetSubcampaigns()),

  dispatchSaveCampaignBrief: (profileId: number, briefDetails: CampaignForm) =>
    dispatch(saveCampaignBrief(profileId, briefDetails)),

  dispatchSaveAssets: (campaignId: number) => dispatch(saveAssets(campaignId)),
  dispatchShowConfirmationModal: (params: ConfirmationModalParams) => (
    dispatch(showConfirmModal(params))
  ),
  dispatchHideConfirmationModal: () => (
    dispatch(hideConfirmModal())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignCreate);
